import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, CalendarDays, Briefcase, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/Shell";
import { api } from "@/lib/api";
import { fmtDate, todayLocal } from "@/lib/format";
import { usePay } from "@/context/CompanyContext";
import { weekStartDate, weekDayLabels } from "@/lib/payPeriod";

const iso = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };

export const MyRosterTab = () => {
  const [schedule, setSchedule] = useState([]);
  const [weekJobs, setWeekJobs] = useState([]);
  const [timeoff, setTimeoff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ start_date: todayLocal(), end_date: todayLocal(), reason: "" });
  const pay = usePay();
  const DAYS = weekDayLabels(pay);
  const weekStart = weekStartDate(new Date(), pay);

  const load = async () => {
    try {
      const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 6);
      const [s, t, wj] = await Promise.all([
        api.mySchedule(iso(weekStart)),
        api.myTimeOff(),
        api.myWeekJobs(iso(weekStart), iso(weekEnd)).catch(() => []),
      ]);
      setSchedule(s); setTimeoff(t); setWeekJobs(wj || []);
    } catch { toast.error("Failed to load roster"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pay.start_day, pay.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    try { await api.applyTimeOff(form); toast.success("Request submitted"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit request"); }
  };

  const respond = async (id, status) => {
    try {
      await api.respondShift(id, status);
      toast.success(status === "accepted" ? "Shift accepted" : "Shift declined");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update shift"); }
  };

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const byDate = (d) => schedule.filter((x) => x.date === iso(d));
  const jobsByDate = (d) => weekJobs.filter((x) => x.scheduled_date === iso(d));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="fade-up">
      <h2 className="font-display text-2xl font-bold mb-1">My Week</h2>
      <p className="text-sm text-muted-foreground mb-6">Your roster and leave requests.</p>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3 mb-10">
        {days.map((d, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-3 min-h-[110px]">
            <p className="text-xs font-semibold text-muted-foreground mb-2">{DAYS[i]} <span className="font-mono">{d.getDate()}</span></p>
            {byDate(d).length === 0 && jobsByDate(d).length === 0 && (
              <p className="text-[11px] text-muted-foreground/50 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Off</p>
            )}
            {byDate(d).map((s) => {
              const casual = s.employment_type === "casual";
              const st = s.status;
              return (
              <div key={s.id} data-testid={`myweek-shift-${s.id}`} className={`rounded-lg border px-2.5 py-2 mb-1.5 ${st === "declined" ? "bg-secondary/40 border-border opacity-70" : st === "accepted" ? "bg-emerald-500/10 border-emerald-500/25" : casual && st === "pending" ? "bg-amber-500/10 border-amber-500/30" : "bg-primary/10 border-primary/20"}`}>
                <p className={`text-[11px] font-mono font-semibold ${st === "declined" ? "line-through" : ""}`}>{s.start_time}–{s.end_time}</p>
                {s.note && <p className="text-[11px] text-muted-foreground truncate">{s.note}</p>}
                {casual && st === "pending" && (
                  <div className="flex gap-1.5 mt-1.5" data-testid={`shift-actions-${s.id}`}>
                    <Button size="sm" data-testid={`accept-shift-${s.id}`} onClick={() => respond(s.id, "accepted")} className="h-6 px-2 text-[11px] rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-3 h-3 mr-0.5" /> Accept</Button>
                    <Button size="sm" variant="outline" data-testid={`decline-shift-${s.id}`} onClick={() => respond(s.id, "declined")} className="h-6 px-2 text-[11px] rounded-full"><X className="w-3 h-3 mr-0.5" /> Decline</Button>
                  </div>
                )}
                {casual && st === "accepted" && <p className="text-[10px] font-semibold text-emerald-600 mt-1 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Accepted</p>}
                {casual && st === "declined" && (
                  <div className="mt-1">
                    <p className="text-[10px] font-semibold text-red-600 inline-flex items-center gap-1"><X className="w-3 h-3" /> Declined</p>
                    <button type="button" data-testid={`reaccept-shift-${s.id}`} onClick={() => respond(s.id, "accepted")} className="block text-[10px] text-muted-foreground underline mt-0.5">Accept instead</button>
                  </div>
                )}
              </div>
              );
            })}
            {jobsByDate(d).map((j) => (
              <div key={j.id} data-testid={`myweek-job-${j.id}`} className="rounded-lg bg-amber-500/10 border border-amber-500/25 px-2.5 py-2 mb-1.5">
                <p className="text-[11px] font-semibold truncate flex items-center gap-1"><Briefcase className="w-3 h-3 shrink-0" /> {j.name}</p>
                {(j.client || j.address) && <p className="text-[11px] text-muted-foreground truncate">{j.client || j.address}</p>}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-semibold">Time off</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="request-timeoff-btn" className="rounded-full bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" /> Request</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-display text-xl">Request time off</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>From</Label><Input data-testid="timeoff-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div className="space-y-2"><Label>To</Label><Input data-testid="timeoff-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Reason</Label><Input data-testid="timeoff-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Optional" /></div>
            </div>
            <DialogFooter><Button data-testid="timeoff-submit" onClick={apply} className="rounded-full bg-primary hover:bg-primary/90 text-white">Submit request</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {timeoff.length === 0 ? <p className="text-sm text-muted-foreground">No requests yet.</p> : (
        <div className="space-y-3">
          {timeoff.map((r) => (
            <div key={r.id} data-testid={`my-timeoff-${r.id}`} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="font-mono text-sm">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</p>
                {r.reason && <p className="text-xs text-muted-foreground mt-0.5">{r.reason}</p>}
              </div>
              <StatusBadge status={r.status === "denied" ? "denied" : r.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
